import * as Sentry from '@sentry/react';

/**
 * Browser-side Sentry init. Gated on VITE_SENTRY_DSN — when unset, Sentry is
 * inactive for local dev / Sentry-free deploys.
 *
 * Called once from main.tsx before render. Idempotent.
 *
 * NOTE: react-router instrumentation is not wired here. The app uses the
 * legacy <BrowserRouter> component (not createBrowserRouter), and Sentry's
 * router-tracing helpers are aimed at the data-router APIs. If we migrate to
 * createBrowserRouter we should add reactRouterV6BrowserTracingIntegration
 * here and wrap the router with wrapCreateBrowserRouterV6.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    // Browser-side perf tracing. Light by default — bump in prod once you see
    // the event volume.
    tracesSampleRate: 0.2,
    // Session replay catches 1% of all sessions plus 100% of errored ones.
    // The math: 1000 sessions/day at 1% = 10 replays + however many errors,
    // which is well under Sentry free-tier replay caps.
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      if (event.request?.headers) {
        for (const key of Object.keys(event.request.headers)) {
          if (/^(authorization|cookie|set-cookie|x-admin-key)$/i.test(key)) {
            delete event.request.headers[key];
          }
        }
      }
      return event;
    },
  });
}

export { Sentry };
